/**
 * batchStore — module-level singleton that holds the state of an ongoing
 * batch prepare/publish operation. Lives outside React's component tree so
 * navigating away from /publish does NOT cancel the running operation.
 *
 * Consumers subscribe on mount and re-render when state changes.
 * The BatchPublish component uses this store to persist rows + progress +
 * running flags across route transitions.
 */
const STORAGE_KEY = "magic_sip_batch_state_v1";

function loadFromStorage() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Hard reload should reset in-flight flags; keep data.
    return { ...parsed, preparing: false, publishing: false };
  } catch { return null; }
}

function saveToStorage(s) {
  try {
    if (typeof localStorage === "undefined") return;
    const snap = {
      rows: s.rows,
      fileName: s.fileName,
      inputText: s.inputText,
      headersDetected: s.headersDetected,
      prepProgress: s.prepProgress,
      pubProgress: s.pubProgress,
      prepTiming: s.prepTiming,
      pubTiming: s.pubTiming,
      llmCost: s.llmCost,
      batchId: s.batchId,
      batchName: s.batchName,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch { /* quota or SSR */ }
}

const listeners = new Set();

let state = loadFromStorage() || {
  rows: [],
  fileName: "",
  inputText: "",
  headersDetected: false,
  prepProgress: { done: 0, total: 0 },
  pubProgress: { done: 0, total: 0 },
  preparing: false,
  publishing: false,
  prepTiming: null,
  pubTiming: null,
  llmCost: 0,
  abortController: null,
  batchId: null,     // set when this state was loaded from a saved /batches entry
  batchName: "",
};

export const batchStore = {
  getState: () => state,

  setState: (patch) => {
    if (typeof patch === "function") state = { ...state, ...patch(state) };
    else state = { ...state, ...patch };
    saveToStorage(state);
    listeners.forEach((fn) => fn(state));
  },

  subscribe: (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /** Load a fully-formed snapshot from a saved batch. Replaces current state. */
  loadFromBatch: (snapshot) => {
    state = {
      rows: snapshot.rows || [],
      fileName: snapshot.fileName || "",
      inputText: "",
      headersDetected: !!snapshot.headersDetected,
      prepProgress: snapshot.prepProgress || { done: 0, total: 0 },
      pubProgress: snapshot.pubProgress || { done: 0, total: 0 },
      preparing: false,
      publishing: false,
      prepTiming: null,
      pubTiming: null,
      llmCost: snapshot.llmCost || 0,
      abortController: null,
      batchId: snapshot.batchId || null,
      batchName: snapshot.batchName || "",
    };
    saveToStorage(state);
    listeners.forEach((fn) => fn(state));
  },

  reset: () => {
    // Do NOT reset if preparing/publishing is still running
    if (state.preparing || state.publishing) return false;
    state = {
      rows: [],
      fileName: "",
      inputText: "",
      headersDetected: false,
      prepProgress: { done: 0, total: 0 },
      pubProgress: { done: 0, total: 0 },
      preparing: false,
      publishing: false,
      prepTiming: null,
      pubTiming: null,
      llmCost: 0,
      abortController: null,
      batchId: null,
      batchName: "",
    };
    listeners.forEach((fn) => fn(state));
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    return true;
  },
};

// Convenience hook for React components
import { useEffect, useState } from "react";
export function useBatchStore() {
  const [snap, setSnap] = useState(batchStore.getState());
  useEffect(() => {
    setSnap(batchStore.getState());
    return batchStore.subscribe(setSnap);
  }, []);
  return snap;
}
